import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, SectionLabel, SerifHeading } from "@/components/site/primitives";
import { SERVICES, SITE } from "@/lib/site-data";

export default function ServicesPage() {
  return (
    <div data-testid="page-services" className="pt-32 md:pt-40 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <SectionLabel>Services</SectionLabel>
            <SerifHeading className="mt-6">
              Practice areas, meticulously handled.
            </SerifHeading>
          </div>
          <div className="lg:col-span-5">
            <p className="text-muted-foreground leading-relaxed">
              Every engagement — from a single contract review to full land
              due diligence — is delivered with clear documentation, honest
              risk framing, and commercially aware advice.
            </p>
          </div>
        </div>

        <div className="mt-20 space-y-16">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.id} delay={0.05}>
              <article
                data-testid={`service-${s.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 border-t border-border pt-12"
              >
                <div className="lg:col-span-1">
                  <div className="font-serif text-3xl text-gold">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <h2 className="font-serif text-3xl md:text-4xl text-navy leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-muted-foreground leading-relaxed max-w-lg">
                    {s.summary}
                  </p>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.id}`}
                    className="mt-8 inline-flex items-center gap-3 text-navy text-xs uppercase tracking-[0.24em] font-semibold link-underline"
                  >
                    Enquire about {s.title}
                    <ArrowRight size={14} />
                  </Link>
                </div>
                <div className="lg:col-span-6">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {s.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-start gap-3 text-sm text-navy py-2 border-b border-border/70"
                      >
                        <Check size={14} className="mt-1 text-gold flex-shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <section className="mt-24 relative overflow-hidden border border-border">
          <img
            src={SITE.images.documents}
            alt="Legal documents on a table"
            className="w-full h-72 md:h-96 object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="px-8 md:px-16 max-w-2xl">
              <div className="eyebrow text-gold">Custom Engagements</div>
              <h3 className="font-serif text-3xl md:text-5xl text-white mt-4 leading-tight">
                Need a bespoke agreement drafted from scratch?
              </h3>
              <Link
                to="/contact"
                data-testid="services-custom-cta"
                className="mt-8 inline-flex items-center gap-3 bg-white text-navy px-6 py-3 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-gold transition-colors"
              >
                Start a Conversation
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
